import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Mail,
  Clock,
  MessageSquare,
  ChevronRight,
  ArrowUpRight,
  MapPin,
  HelpCircle
} from 'lucide-react';
import UserNavbar from '../components/UserNavbar';
import Footer from '../components/Footer';

const Contact = () => {
  const faqs = [
    {
      q: 'How long does it take to get a response?',
      a: 'We typically respond to all emails within 24-48 hours during business days.'
    },
    {
      q: 'What should I include in my email?',
      a: 'Please include your username, order ID (if applicable), and a detailed description of your issue.'
    },
    {
      q: 'Can I get help with technical issues?',
      a: 'Yes! Our support team can help with extension installation, login issues, and other technical problems.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#111111]">
      <UserNavbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">SUPPORT</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 tracking-tight">
            Contact Us
          </h1>
          <p className="text-white/60 text-lg max-w-2xl">
            Have questions or need assistance? We're here to help you with your NeoPass Learning experience.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            
            {/* Left - Contact Methods */}
            <div>
              <h2 className="text-2xl font-medium text-white mb-8">Get in Touch</h2>
              
              {/* Email */}
              <div className="border border-white/10 bg-white/[0.02] p-8 rounded-lg mb-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-accent\/10 rounded-full">
                    <Mail className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs font-mono tracking-widest mb-1">EMAIL</p>
                    <a 
                      href="mailto:neopassbusiness@gmail.com" 
                      className="text-xl text-white hover:text-accent transition-colors"
                    >
                      neopassbusiness@gmail.com
                    </a>
                  </div>
                </div>
                <p className="text-white/60 text-sm">
                  For all inquiries including technical support, billing questions, and refund requests.
                </p>
              </div>

              {/* Response Time */}
              <div className="border border-white/10 bg-white/[0.02] p-8 rounded-lg mb-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-accent\/10 rounded-full">
                    <Clock className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs font-mono tracking-widest mb-1">RESPONSE TIME</p>
                    <p className="text-xl text-white">24-48 Hours</p>
                  </div>
                </div>
                <p className="text-white/60 text-sm">
                  We aim to respond to all emails within 24-48 hours during business days. For urgent issues, include "URGENT" in your subject line.
                </p>
              </div>

              {/* Business Address */}
              <div className="border border-white/10 bg-white/[0.02] p-8 rounded-lg">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-accent\/10 rounded-full">
                    <MapPin className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs font-mono tracking-widest mb-1">LOCATION</p>
                    <p className="text-xl text-white">India</p>
                  </div>
                </div>
                <p className="text-white/60 text-sm">
                  NeoPass Learning is an ed-tech platform based in India, serving students worldwide.
                </p>
              </div>
            </div>

            {/* Right - Quick Help */}
            <div>
              <h2 className="text-2xl font-medium text-white mb-8">Quick Help</h2>
              
              {/* Common Topics */}
              <div className="space-y-4 mb-12">
                <p className="text-white/60 text-sm mb-6">Select a topic for quick assistance:</p>
                
                <a 
                  href="mailto:neopassbusiness@gmail.com?subject=Technical Support Request"
                  className="flex items-center justify-between p-4 border border-white/10 bg-white/[0.02] hover:border-white/20 transition-colors group rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-5 w-5 text-accent" />
                    <span className="text-white/80 group-hover:text-white transition-colors">Technical Support</span>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-white/40 group-hover:text-white transition-colors" />
                </a>

                <a 
                  href="mailto:neopassbusiness@gmail.com?subject=Billing Question"
                  className="flex items-center justify-between p-4 border border-white/10 bg-white/[0.02] hover:border-white/20 transition-colors group rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-5 w-5 text-accent" />
                    <span className="text-white/80 group-hover:text-white transition-colors">Billing Question</span>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-white/40 group-hover:text-white transition-colors" />
                </a>

                <a 
                  href="mailto:neopassbusiness@gmail.com?subject=Account Issue"
                  className="flex items-center justify-between p-4 border border-white/10 bg-white/[0.02] hover:border-white/20 transition-colors group rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-5 w-5 text-accent" />
                    <span className="text-white/80 group-hover:text-white transition-colors">Account Issue</span>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-white/40 group-hover:text-white transition-colors" />
                </a>

                <a 
                  href="mailto:neopassbusiness@gmail.com?subject=General Inquiry"
                  className="flex items-center justify-between p-4 border border-white/10 bg-white/[0.02] hover:border-white/20 transition-colors group rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-5 w-5 text-accent" />
                    <span className="text-white/80 group-hover:text-white transition-colors">General Inquiry</span>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-white/40 group-hover:text-white transition-colors" />
                </a>
              </div>

              {/* FAQ */}
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <HelpCircle className="h-5 w-5 text-accent" />
                  <h3 className="text-lg font-medium text-white">Frequently Asked</h3>
                </div>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div key={index} className="p-4 border border-white/10 bg-white/[0.02] rounded-lg">
                      <h4 className="text-white/90 font-medium mb-2">{faq.q}</h4>
                      <p className="text-white/50 text-sm">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-6">
            Ready to start learning?
          </h2>
          <p className="text-white/50 mb-10 max-w-xl mx-auto">
            Create a free account and explore our AI-powered learning assistant today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-medium hover:bg-white/90 transition-colors"
            >
              Create Account
              <ChevronRight className="h-4 w-4" />
            </Link>
            <Link 
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white font-medium hover:bg-white/5 transition-colors"
            >
              View Credit Packs
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer variant="full" activeLink="contact" />
    </div>
  );
};

export default Contact;
